import React, { useCallback, useDeferredValue, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockResetIcon from '@mui/icons-material/LockReset';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SaveIcon from '@mui/icons-material/Save';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { pageColors } from './HomePage';
import {
  createUser,
  fetchUserById,
  fetchUsers,
  resetUserPassword,
  updateUser,
} from '../api/userManagement';
import TenantPickerForm from '../components/TenantPickerForm';

function formatRole(role) {
  return role ? role.replace(/_/g, ' ') : 'Unknown';
}

function formatDate(value) {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
}

function formatLastLoginDate(value) {
  if (!value) {
    return 'Never logged in';
  }

  return formatDate(value);
}

function createCreateDraft(isSuperAdmin) {
  return {
    mode: isSuperAdmin ? 'tenant' : 'tenant',
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'USER',
    tenantId: '',
    active: true,
  };
}

function createEditDraft(user) {
  return {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    role: user?.role || 'USER',
    active: Boolean(user?.active),
  };
}

function getListRoleOptions(currentUser) {
  if (currentUser?.role === 'SUPER_ADMIN') {
    return ['', 'USER', 'TENANT_ADMIN', 'SUPER_ADMIN'];
  }

  return ['', 'USER', 'TENANT_ADMIN'];
}

function getCreateRoleOptions(mode) {
  return mode === 'global' ? ['SUPER_ADMIN'] : ['USER', 'TENANT_ADMIN'];
}

function getEditRoleOptions(currentUser, selectedUser) {
  if (!selectedUser || !currentUser) {
    return [];
  }

  if (selectedUser.id === currentUser.id) {
    return [selectedUser.role];
  }

  if (selectedUser.tenantId == null) {
    return ['SUPER_ADMIN'];
  }

  return ['USER', 'TENANT_ADMIN'];
}

function removesActiveTenantAdmin(selectedUser, editDraft) {
  return Boolean(
    selectedUser?.tenantId
    && selectedUser?.role === 'TENANT_ADMIN'
    && selectedUser?.active
    && (editDraft.role !== 'TENANT_ADMIN' || !editDraft.active)
  );
}

export default function UsersPage({ currentUser }) {
  const [searchInput, setSearchInput] = useState('');
  const deferredSearch = useDeferredValue(searchInput.trim());
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('active');
  const [users, setUsers] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [listError, setListError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [editorMode, setEditorMode] = useState('empty');
  const [createDraft, setCreateDraft] = useState(() => createCreateDraft(currentUser?.role === 'SUPER_ADMIN'));
  const [editDraft, setEditDraft] = useState(createEditDraft(null));
  const [editorFeedback, setEditorFeedback] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [passwordDraft, setPasswordDraft] = useState('');
  const [passwordFeedback, setPasswordFeedback] = useState(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({
    createPassword: false,
    resetPassword: false,
  });

  const resolvedActiveFilter =
    activeFilter === 'all' ? null : activeFilter === 'inactive' ? false : true;
  const usersColumnsSx = {
    xs: 'minmax(0, 1fr)',
    lg: 'minmax(0, 4fr) minmax(0, 8fr)',
  };
  const responsiveChipSx = {
    maxWidth: '100%',
    height: 'auto',
    '& .MuiChip-label': {
      display: 'block',
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      py: 0.75,
    },
  };

  const togglePasswordVisibility = useCallback((field) => {
    setPasswordVisibility((currentVisibility) => ({
      ...currentVisibility,
      [field]: !currentVisibility[field],
    }));
  }, []);

  const loadUsers = useCallback(async () => {
    setIsLoadingList(true);
    setListError('');

    try {
      const nextUsers = await fetchUsers({
        search: deferredSearch,
        role: roleFilter,
        active: resolvedActiveFilter,
      });
      setUsers(nextUsers);
    } catch (error) {
      setListError(error.message || 'Failed to load users.');
      setUsers([]);
    } finally {
      setIsLoadingList(false);
    }
  }, [deferredSearch, resolvedActiveFilter, roleFilter]);

  const loadUserDetail = useCallback(async (userId) => {
    setIsLoadingDetail(true);
    setDetailError('');

    try {
      const user = await fetchUserById(userId);
      setSelectedUser(user);
      setSelectedUserId(user.id);
      setEditDraft(createEditDraft(user));
      setEditorMode('edit');
      setPasswordDraft('');
      setPasswordFeedback(null);
      setPasswordVisibility((currentVisibility) => ({
        ...currentVisibility,
        resetPassword: false,
      }));
      return user;
    } catch (error) {
      setDetailError(error.message || 'Failed to load the selected user.');
      throw error;
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!selectedUserId || editorMode !== 'edit') {
      return;
    }

    if (!users.some((user) => user.id === selectedUserId)) {
      setSelectedUserId(null);
      setSelectedUser(null);
      setEditorMode('empty');
      setDetailError('');
      setEditorFeedback(null);
    }
  }, [editorMode, selectedUserId, users]);

  const handleSelectUser = async (userId) => {
    setEditorFeedback(null);

    try {
      await loadUserDetail(userId);
    } catch (error) {
      console.error('Failed to load user detail:', error);
    }
  };

  const handleStartCreate = () => {
    setSelectedUserId(null);
    setSelectedUser(null);
    setDetailError('');
    setEditorFeedback(null);
    setPasswordFeedback(null);
    setPasswordDraft('');
    setPasswordVisibility({
      createPassword: false,
      resetPassword: false,
    });
    setCreateDraft(createCreateDraft(currentUser?.role === 'SUPER_ADMIN'));
    setEditorMode('create');
  };

  const handleSubmitCreate = async (event) => {
    event.preventDefault();
    setEditorFeedback(null);

    const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';
    const createMode = isSuperAdmin ? createDraft.mode : 'tenant';
    const role = createMode === 'global' ? 'SUPER_ADMIN' : createDraft.role;

    if (isSuperAdmin && createMode === 'tenant' && !createDraft.tenantId) {
      setEditorFeedback({ severity: 'error', message: 'Please choose a tenant.' });
      return;
    }

    setIsSaving(true);

    try {
      const createdUser = await createUser({
        username: createDraft.username.trim(),
        password: createDraft.password,
        firstName: createDraft.firstName.trim(),
        lastName: createDraft.lastName.trim(),
        email: createDraft.email.trim(),
        phone: createDraft.phone.trim() || null,
        role,
        tenantId:
          !isSuperAdmin ? undefined : createMode === 'global' ? null : Number(createDraft.tenantId),
        active: createDraft.active,
      });

      await loadUsers();
      await loadUserDetail(createdUser.id);
      setEditorFeedback({ severity: 'success', message: 'User created successfully.' });
    } catch (error) {
      setEditorFeedback({ severity: 'error', message: error.message || 'Failed to create the user.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitEdit = async (event) => {
    event.preventDefault();

    if (!selectedUser) {
      return;
    }

    setEditorFeedback(null);

    if (currentUser?.role === 'SUPER_ADMIN' && removesActiveTenantAdmin(selectedUser, editDraft)) {
      try {
        const activeTenantAdmins = await fetchUsers({ role: 'TENANT_ADMIN', active: true });
        const activeAdminsForTenant = activeTenantAdmins.filter((user) => user.tenantId === selectedUser.tenantId);
        if (activeAdminsForTenant.length <= 1) {
          const confirmed = window.confirm(
            'This is the last active tenant admin for this tenant. If you continue, the tenant will have no active tenant admin. Only a super-admin will be able to restore access or continue tenant cleanup.'
          );
          if (!confirmed) {
            return;
          }
        }
      } catch (error) {
        setEditorFeedback({
          severity: 'error',
          message: error.message || 'Failed to verify tenant admin status.',
        });
        return;
      }
    }

    setIsSaving(true);

    try {
      await updateUser(selectedUser.id, {
        firstName: editDraft.firstName.trim(),
        lastName: editDraft.lastName.trim(),
        email: editDraft.email.trim(),
        phone: editDraft.phone.trim() || null,
        role: editDraft.role,
        active: editDraft.active,
      });

      await loadUsers();
      await loadUserDetail(selectedUser.id);
      setEditorFeedback({ severity: 'success', message: 'User updated successfully.' });
    } catch (error) {
      setEditorFeedback({ severity: 'error', message: error.message || 'Failed to update the user.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();

    if (!selectedUser) {
      return;
    }

    if (!passwordDraft.trim()) {
      setPasswordFeedback({ severity: 'error', message: 'Please enter a new password.' });
      return;
    }

    setPasswordFeedback(null);
    setIsResettingPassword(true);

    try {
      await resetUserPassword(selectedUser.id, passwordDraft.trim());
      setPasswordDraft('');
      setPasswordFeedback({ severity: 'success', message: 'Password reset successfully.' });
    } catch (error) {
      setPasswordFeedback({ severity: 'error', message: error.message || 'Failed to reset the password.' });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const renderListPanel = () => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 4,
        backgroundColor: '#fff',
        boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
        height: '100%',
      }}
    >
      <Stack spacing={2.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Directory
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Filter visible users and open the detail editor.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleStartCreate}
            sx={{ borderRadius: '999px', textTransform: 'none' }}
          >
            Create user
          </Button>
        </Stack>

        <TextField
          label="Search"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Name, username, email..."
          fullWidth
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            select
            label="Role"
            value={roleFilter}
            onChange={(event) => setRoleFilter(event.target.value)}
            fullWidth
          >
            <MenuItem value="">All roles</MenuItem>
            {getListRoleOptions(currentUser).filter(Boolean).map((role) => (
              <MenuItem key={role} value={role}>
                {formatRole(role)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Active"
            value={activeFilter}
            onChange={(event) => setActiveFilter(event.target.value)}
            fullWidth
          >
            <MenuItem value="active">Active only</MenuItem>
            <MenuItem value="inactive">Inactive only</MenuItem>
            <MenuItem value="all">All users</MenuItem>
          </TextField>
        </Stack>

        {isLoadingList && <LinearProgress />}
        {listError && <Alert severity="error">{listError}</Alert>}

        <List sx={{ p: 0 }}>
          {users.length === 0 && !isLoadingList && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: '1px dashed rgba(0,0,0,0.15)',
                backgroundColor: 'rgba(0,0,0,0.02)',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No users match the current filters.
              </Typography>
            </Paper>
          )}

          {users.map((user) => (
            <ListItemButton
              key={user.id}
              selected={selectedUserId === user.id && editorMode === 'edit'}
              onClick={() => handleSelectUser(user.id)}
              sx={{
                mb: 1.25,
                borderRadius: 3,
                border: '1px solid rgba(0,0,0,0.06)',
                alignItems: 'flex-start',
              }}
            >
              <Stack direction="row" spacing={1.5} sx={{ width: '100%' }}>
                <Avatar sx={{ bgcolor: `${pageColors.users || '#1565c0'}20`, color: pageColors.users || '#1565c0' }}>
                  {user.displayName?.charAt(0)?.toUpperCase() || user.username?.charAt(0)?.toUpperCase()}
                </Avatar>
                <Stack spacing={0.75} sx={{ width: '100%', minWidth: 0 }}>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {user.displayName}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" noWrap>
                        @{user.username} · {user.company}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={formatRole(user.role)} size="small" variant="outlined" />
                    <Chip
                      label={user.active ? 'Active' : 'Inactive'}
                      size="small"
                      color={user.active ? 'success' : 'default'}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Last login: {formatLastLoginDate(user.lastLoginAt)}
                  </Typography>
                </Stack>
              </Stack>
            </ListItemButton>
          ))}
        </List>
      </Stack>
    </Paper>
  );

  const renderCreatePanel = () => {
    const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';
    const createMode = isSuperAdmin ? createDraft.mode : 'tenant';
    const roleOptions = getCreateRoleOptions(createMode);
    const isTenantCreateMissingTenant = isSuperAdmin && createMode === 'tenant' && !createDraft.tenantId;
    const isCreateDisabled = isSaving || isTenantCreateMissingTenant;

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: 4,
          backgroundColor: '#fff',
          boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
          height: '100%',
        }}
      >
        <Stack spacing={3} component="form" onSubmit={handleSubmitCreate}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Create user
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add a new account using the backend role and tenant rules.
              </Typography>
            </Box>
            <Chip
              label={currentUser?.role === 'SUPER_ADMIN' ? 'Global admin scope' : `Tenant: ${currentUser?.company}`}
              color="primary"
              variant="outlined"
            />
          </Stack>

          {isSuperAdmin && (
            <ToggleButtonGroup
              exclusive
              value={createDraft.mode}
              onChange={(_event, nextMode) => {
                if (!nextMode) {
                  return;
                }

                setCreateDraft((currentDraft) => ({
                  ...currentDraft,
                  mode: nextMode,
                  role: nextMode === 'global' ? 'SUPER_ADMIN' : 'USER',
                  tenantId: nextMode === 'global' ? '' : currentDraft.tenantId,
                }));
              }}
              size="small"
            >
              <ToggleButton value="tenant">Tenant user</ToggleButton>
              <ToggleButton value="global">Global super admin</ToggleButton>
            </ToggleButtonGroup>
          )}

          {editorFeedback && <Alert severity={editorFeedback.severity}>{editorFeedback.message}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Username"
              value={createDraft.username}
              onChange={(event) => setCreateDraft({ ...createDraft, username: event.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Password"
              type={passwordVisibility.createPassword ? 'text' : 'password'}
              value={createDraft.password}
              onChange={(event) => setCreateDraft({ ...createDraft, password: event.target.value })}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="Toggle create password visibility"
                      onClick={() => togglePasswordVisibility('createPassword')}
                    >
                      {passwordVisibility.createPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
              required
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="First name"
              value={createDraft.firstName}
              onChange={(event) => setCreateDraft({ ...createDraft, firstName: event.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Last name"
              value={createDraft.lastName}
              onChange={(event) => setCreateDraft({ ...createDraft, lastName: event.target.value })}
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Email"
              type="email"
              value={createDraft.email}
              onChange={(event) => setCreateDraft({ ...createDraft, email: event.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Phone"
              value={createDraft.phone}
              onChange={(event) => setCreateDraft({ ...createDraft, phone: event.target.value })}
              fullWidth
            />
          </Stack>

          {isSuperAdmin && createMode === 'tenant' && (
            <TextField
              select
              label="Tenant role"
              value={createDraft.role}
              onChange={(event) => setCreateDraft({ ...createDraft, role: event.target.value })}
              fullWidth
            >
              {roleOptions.map((role) => (
                <MenuItem key={role} value={role}>
                  {formatRole(role)}
                </MenuItem>
              ))}
            </TextField>
          )}

          {!isSuperAdmin && (
            <TextField
              select
              label="Role"
              value={createDraft.role}
              onChange={(event) => setCreateDraft({ ...createDraft, role: event.target.value })}
              fullWidth
            >
              {roleOptions.map((role) => (
                <MenuItem key={role} value={role}>
                  {formatRole(role)}
                </MenuItem>
              ))}
            </TextField>
          )}

          {isSuperAdmin && createMode === 'global' && (
            <Alert severity="info">
              Global super-admin mode locks the role to <strong>SUPER ADMIN</strong> and uses company{' '}
              <strong>__system__</strong>.
            </Alert>
          )}

          {isSuperAdmin && createMode === 'tenant' && (
            <TenantPickerForm
              currentTenantId={createDraft.tenantId ? Number(createDraft.tenantId) : null}
              onSelectionChange={async (tenantId) => {
                setCreateDraft((currentDraft) => ({ ...currentDraft, tenantId: tenantId ? String(tenantId) : '' }));
              }}
              label="User tenant"
              helperText="Choose the tenant that the new account should belong to. The selection is applied immediately."
              showSubmitButton={false}
              size="small"
              required
            />
          )}

          {!isSuperAdmin && (
            <Alert severity="info">Tenant assignment is fixed to your own tenant in this create flow.</Alert>
          )}

          {isTenantCreateMissingTenant && (
            <Alert severity="info">Choose a tenant first, then press Create user.</Alert>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={createDraft.active}
                onChange={(event) => setCreateDraft({ ...createDraft, active: event.target.checked })}
              />
            }
            label="Active"
          />

          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isCreateDisabled}
            sx={{ alignSelf: 'flex-start', borderRadius: '999px', textTransform: 'none' }}
          >
            {isSaving ? 'Saving...' : 'Create user'}
          </Button>
        </Stack>
      </Paper>
    );
  };

  const renderEditPanel = () => {
    if (!selectedUser) {
      return null;
    }

    const editRoleOptions = getEditRoleOptions(currentUser, selectedUser);
    const isSelf = currentUser?.id === selectedUser.id;

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: 4,
          backgroundColor: '#fff',
          boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
          height: '100%',
        }}
      >
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {selectedUser.displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                @{selectedUser.username} · {selectedUser.company}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={formatRole(selectedUser.role)} color="primary" variant="outlined" />
              <Chip label={selectedUser.active ? 'Active' : 'Inactive'} color={selectedUser.active ? 'success' : 'default'} />
            </Stack>
          </Stack>

          {detailError && <Alert severity="error">{detailError}</Alert>}
          {editorFeedback && <Alert severity={editorFeedback.severity}>{editorFeedback.message}</Alert>}
          {isLoadingDetail && <LinearProgress />}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Username" value={selectedUser.username} fullWidth disabled />
            <TextField label="Company / tenant" value={selectedUser.company || '__system__'} fullWidth disabled />
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <TextField
              label="Last login"
              value={formatLastLoginDate(selectedUser.lastLoginAt)}
              fullWidth
              disabled
            />
          </Box>

          <Stack component="form" spacing={2} onSubmit={handleSubmitEdit}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First name"
                value={editDraft.firstName}
                onChange={(event) => setEditDraft({ ...editDraft, firstName: event.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Last name"
                value={editDraft.lastName}
                onChange={(event) => setEditDraft({ ...editDraft, lastName: event.target.value })}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Email"
                type="email"
                value={editDraft.email}
                onChange={(event) => setEditDraft({ ...editDraft, email: event.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Phone"
                value={editDraft.phone}
                onChange={(event) => setEditDraft({ ...editDraft, phone: event.target.value })}
                fullWidth
              />
            </Stack>

            <TextField
              select
              label="Role"
              value={editDraft.role}
              onChange={(event) => setEditDraft({ ...editDraft, role: event.target.value })}
              disabled={editRoleOptions.length <= 1}
              fullWidth
            >
              {editRoleOptions.map((role) => (
                <MenuItem key={role} value={role}>
                  {formatRole(role)}
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={editDraft.active}
                  onChange={(event) => setEditDraft({ ...editDraft, active: event.target.checked })}
                  disabled={isSelf}
                />
              }
              label={isSelf ? 'Active (self-disable blocked)' : 'Active'}
            />

            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={isSaving}
              sx={{ alignSelf: 'flex-start', borderRadius: '999px', textTransform: 'none' }}
            >
              {isSaving ? 'Saving...' : 'Save changes'}
            </Button>
          </Stack>

          <Divider />

          <Stack spacing={2} component="form" onSubmit={handleResetPassword}>
            <Stack direction="row" spacing={1} alignItems="center">
              <LockResetIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={600}>
                Reset password
              </Typography>
            </Stack>

            {passwordFeedback && <Alert severity={passwordFeedback.severity}>{passwordFeedback.message}</Alert>}

            <TextField
              label="New password"
              type={passwordVisibility.resetPassword ? 'text' : 'password'}
              value={passwordDraft}
              onChange={(event) => setPasswordDraft(event.target.value)}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="Toggle reset password visibility"
                      onClick={() => togglePasswordVisibility('resetPassword')}
                    >
                      {passwordVisibility.resetPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
            />

            <Button
              type="submit"
              variant="outlined"
              disabled={isResettingPassword}
              sx={{ alignSelf: 'flex-start', borderRadius: '999px', textTransform: 'none' }}
            >
              {isResettingPassword ? 'Resetting...' : 'Reset password'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const renderPlaceholderPanel = () => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 4,
        backgroundColor: '#fff',
        boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
        height: '100%',
      }}
    >
      <Stack spacing={2} alignItems="flex-start">
        <Avatar sx={{ bgcolor: `${pageColors.users || '#1565c0'}20`, color: pageColors.users || '#1565c0' }}>
          <PersonOutlineIcon />
        </Avatar>
        <Typography variant="h6" fontWeight={700}>
          Select a user or create a new one
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Open a list row to edit an existing account, or start a create flow from the left panel.
        </Typography>
      </Stack>
    </Paper>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at top left, rgba(21,101,192,0.14), transparent 55%), #f7f9fb',
        py: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              py: { xs: 2.5, md: 3 },
              borderRadius: 4,
              backgroundColor: '#fff',
              boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
            }}
          >
            <Box
              sx={{
                px: { xs: 2.5, md: 3 },
                display: 'grid',
                gridTemplateColumns: usersColumnsSx,
                gap: 3,
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Avatar sx={{ bgcolor: pageColors.users || '#1565c0', color: '#fff', width: 56, height: 56 }}>
                      <ManageAccountsIcon />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="overline" sx={{ color: pageColors.users || '#1565c0', letterSpacing: 3 }}>
                        ADMINISTRATION
                      </Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                        Users
                      </Typography>
                    </Box>
                  </Stack>

                  <Typography variant="body1" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                    Search, create, update, and reset passwords using the live backend user-management contracts.
                  </Typography>
                </Stack>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Stack spacing={1.25} alignItems="flex-start">
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ maxWidth: '100%' }}>
                    <Chip label={formatRole(currentUser?.role)} color="primary" variant="outlined" sx={responsiveChipSx} />
                    <Chip
                      label={currentUser?.role === 'SUPER_ADMIN' ? 'Global visibility' : `Tenant: ${currentUser?.company}`}
                      variant="outlined"
                      sx={responsiveChipSx}
                    />
                  </Stack>
                </Stack>
              </Box>
            </Box>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: usersColumnsSx,
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {renderListPanel()}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              {editorMode === 'create'
                ? renderCreatePanel()
                : editorMode === 'edit'
                  ? renderEditPanel()
                  : renderPlaceholderPanel()}
            </Box>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
